/**
 * Vehicles Side Panel Component
 * Collapsible side panel showing list of vehicles/devices
 */

import React, { useState, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Animated,
  Platform,
  useWindowDimensions,
  ActivityIndicator,
} from 'react-native';
import { Ionicons, FontAwesome5 } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useTheme, Text, Surface } from 'react-native-paper';
import type { LivePosition } from '../../types/gps';
import VehicleCard from './VehicleCard';

interface VehiclesSidePanelProps {
  devices: LivePosition[];
  selectedDeviceId?: number;
  onDevicePress: (device: LivePosition) => void;
  onLocateDevice: (device: LivePosition) => void;
  onEditVehicleLink?: (traccarDeviceId: number, vehicleId: number | null) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  isLoading?: boolean;
}

const PANEL_WIDTH = 320;
const COLLAPSED_WIDTH = 48;

export default function VehiclesSidePanel({
  devices,
  selectedDeviceId,
  onDevicePress,
  onLocateDevice,
  onEditVehicleLink,
  isCollapsed = false,
  onToggleCollapse,
  isLoading = false,
}: VehiclesSidePanelProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const { height, width } = useWindowDimensions();
  
  // Calculate available height - account for tab bar (footer) height
  // Tab bar is typically around 50-60px on mobile, 60-70px on web
  // Add extra margin to ensure content is not cut off
  const TAB_BAR_HEIGHT = Platform.OS === 'web' ? 70 : 60;
  const availableHeight = height - TAB_BAR_HEIGHT - 20; // Extra 20px buffer

  // Filter devices based on search
  const filteredDevices = useMemo(() => {
    if (!searchQuery.trim()) return devices;
    
    const query = searchQuery.toLowerCase();
    return devices.filter(device => {
      return (
        device.device_label?.toLowerCase().includes(query) ||
        device.device_imei?.toLowerCase().includes(query) ||
        device.vehicle?.registration_number?.toLowerCase().includes(query)
      );
    });
  }, [devices, searchQuery]);

  // Count online/offline devices
  const onlineCount = devices.filter(d => d.is_online).length;
  const offlineCount = devices.length - onlineCount;

  if (isCollapsed) {
    return (
      <Surface style={[styles.collapsedPanel, { maxHeight: availableHeight, backgroundColor: theme.colors.surface, borderRightColor: theme.colors.outlineVariant }]}>
        <TouchableOpacity
          style={styles.expandButton}
          onPress={onToggleCollapse}
        >
          <Ionicons name="chevron-forward" size={24} color={theme.colors.primary} />
        </TouchableOpacity>
        
        {isLoading ? (
          <View style={styles.collapsedLoading}>
            <ActivityIndicator size="small" color={theme.colors.primary} />
          </View>
        ) : (
          <View style={styles.collapsedStats}>
          <View style={styles.collapsedStatItem}>
            <Text style={[styles.collapsedStatNumber, { color: theme.colors.onSurface }]}>{devices.length}</Text>
            <FontAwesome5 name="car" size={16} color={theme.colors.onSurfaceVariant} solid />
          </View>
            
          <View style={styles.collapsedStatItem}>
            <Text style={[styles.collapsedStatNumber, { color: theme.colors.primary }]}>
              {onlineCount}
            </Text>
            <View style={[styles.miniDot, { backgroundColor: theme.colors.primary }]} />
          </View>
            
            <View style={styles.collapsedStatItem}>
              <Text style={[styles.collapsedStatNumber, { color: theme.colors.onSurfaceVariant }]}>
                {offlineCount}
              </Text>
              <View style={[styles.miniDot, { backgroundColor: theme.colors.onSurfaceVariant }]} />
            </View>
          </View>
        )}
      </Surface>
    );
  }

  return (
    <Surface style={[styles.panel, { maxHeight: availableHeight, backgroundColor: theme.colors.surface, borderRightColor: theme.colors.outlineVariant }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: theme.colors.surface, borderBottomColor: theme.colors.outlineVariant }]}>
        <View style={styles.headerTop}>
          <View style={styles.titleRow}>
            <View style={[styles.iconContainer, { backgroundColor: theme.colors.primary, shadowColor: theme.colors.primary }]}>
              <FontAwesome5 name="car" size={18} color={theme.colors.onPrimary} solid />
            </View>
            <Text variant="titleMedium" style={[styles.title, { color: theme.colors.onSurface }]}>{t('gps.vehicles')}</Text>
            {isLoading ? (
              <View style={[styles.badge, { backgroundColor: theme.colors.primary, shadowColor: theme.colors.primary }]}>
                <ActivityIndicator size="small" color={theme.colors.onPrimary} />
              </View>
            ) : (
              <View style={[styles.badge, { backgroundColor: theme.colors.primary, shadowColor: theme.colors.primary }]}>
                <Text style={[styles.badgeText, { color: theme.colors.onPrimary }]}>{devices.length}</Text>
              </View>
            )}
          </View>
          
          {onToggleCollapse && (
            <TouchableOpacity
              style={styles.collapseButton}
              onPress={onToggleCollapse}
            >
              <Ionicons name="chevron-back" size={20} color={theme.colors.primary} />
            </TouchableOpacity>
          )}
        </View>

        {/* Stats */}
        <View style={styles.stats}>
          <View style={styles.stat}>
            <View style={[styles.statDot, { backgroundColor: theme.colors.primary }]} />
            <Text style={[styles.statText, { color: theme.colors.onSurface }]}>{t('gps.online')}: {onlineCount}</Text>
          </View>
          <View style={styles.stat}>
            <View style={[styles.statDot, { backgroundColor: theme.colors.onSurfaceVariant }]} />
            <Text style={[styles.statText, { color: theme.colors.onSurface }]}>{t('gps.offline')}: {offlineCount}</Text>
          </View>
        </View>

        {/* Search */}
        <View style={[styles.searchContainer, { backgroundColor: theme.colors.surfaceVariant }]}>
          <Ionicons name="search" size={16} color={theme.colors.onSurfaceVariant} style={styles.searchIcon} />
          <TextInput
            style={[styles.searchInput, { color: theme.colors.onSurface }]}
            placeholder={t('gps.searchVehicles')}
            placeholderTextColor={theme.colors.onSurfaceVariant}
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoCapitalize="none"
            autoCorrect={false}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity
              onPress={() => setSearchQuery('')}
              style={styles.clearButton}
            >
              <Ionicons name="close-circle" size={16} color={theme.colors.onSurfaceVariant} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Vehicle List - Scrollable area */}
      <View style={styles.scrollContainer}>
        <ScrollView
          style={styles.list}
          contentContainerStyle={[
            styles.listContent,
            { paddingBottom: 20 } // Padding to ensure last card is visible
          ]}
          showsVerticalScrollIndicator={true}
          nestedScrollEnabled={true}
          bounces={true}
          scrollEnabled={true}
        >
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={theme.colors.primary} />
              <Text style={[styles.loadingText, { color: theme.colors.onSurfaceVariant }]}>{t('gps.loadingVehicles')}</Text>
            </View>
          ) : filteredDevices.length === 0 ? (
            <View style={styles.emptyState}>
              <Ionicons name="car-outline" size={48} color={theme.colors.outline} />
              <Text style={[styles.emptyStateText, { color: theme.colors.onSurfaceVariant }]}>
                {searchQuery ? 'Nenhum veículo encontrado' : 'Nenhum veículo disponível'}
              </Text>
            </View>
          ) : (
            filteredDevices.map((device) => (
              <VehicleCard
                key={device.device_id}
                position={device}
                isSelected={device.device_id === selectedDeviceId}
                onPress={() => onDevicePress(device)}
                onLocate={() => onLocateDevice(device)}
                onEditVehicleLink={
                  onEditVehicleLink
                    ? () => onEditVehicleLink(device.device_id, device.vehicle?.id || null)
                    : undefined
                }
              />
            ))
          )}
        </ScrollView>
      </View>
    </Surface>
  );
}

const styles = StyleSheet.create({
  panel: {
    width: PANEL_WIDTH,
    borderRightWidth: 1,
    flexDirection: 'column',
    ...Platform.select({
      web: {
        boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
        zIndex: 100, // Much higher than tab bar
      },
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 2, height: 0 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        zIndex: 100,
      },
      android: {
        elevation: 20, // Much higher than tab bar (elevation: 8)
      },
    }),
  },
  collapsedPanel: {
    width: COLLAPSED_WIDTH,
    borderRightWidth: 1,
    alignItems: 'center',
    paddingVertical: 16,
  },
  expandButton: {
    padding: 8,
    marginBottom: 24,
  },
  collapsedLoading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
  },
  collapsedStats: {
    gap: 20,
  },
  collapsedStatItem: {
    alignItems: 'center',
    gap: 4,
  },
  collapsedStatNumber: {
    fontSize: 16,
    fontWeight: '700',
  },
  miniDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  iconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 3,
  },
  title: {
    fontWeight: '700',
    flex: 1,
  },
  badge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    minWidth: 28,
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 3,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '700',
  },
  collapseButton: {
    padding: 4,
  },
  stats: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 12,
  },
  stat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  statDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  statText: {
    fontSize: 13,
    fontWeight: '600',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    paddingHorizontal: 10,
    height: 36,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    padding: 0,
  },
  clearButton: {
    padding: 4,
  },
  scrollContainer: {
    flex: 1,
    minHeight: 0, // Critical for flex scrolling
  },
  list: {
    flex: 1,
  },
  listContent: {
    padding: 12,
    paddingBottom: 24,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  loadingText: {
    fontSize: 14,
    marginTop: 12,
    textAlign: 'center',
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  emptyStateText: {
    fontSize: 14,
    marginTop: 12,
    textAlign: 'center',
  },
  footer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderTopWidth: 1,
  },
  footerText: {
    fontSize: 11,
    textAlign: 'center',
  },
});
